/**
 * useTickerNames — shared lookup of full security names by ticker.
 *
 * Fetches /api/tickers (the tickers_bounds map: {ticker: {name, start, end}})
 * once per session and caches at module level, mirroring useFundamentals.
 * Returns a lookup fn: name for the ticker, or null while
 * loading / when the backend has no real name (the backend falls back to the
 * ticker itself — treated as "no name" here so callers can decide their own
 * fallback display).
 */
import { useEffect, useState } from "react";
import requests from "../requests";

let _cache: Record<string, string> | null = null;
let _inflight: Promise<void> | null = null;

export function useTickerNames(): (ticker: string) => string | null {
  const [names, setNames] = useState<Record<string, string> | null>(_cache);

  useEffect(() => {
    if (_cache) { setNames(_cache); return; }

    if (!_inflight) {
      _inflight = requests.get("/api/tickers")
        .then(r => {
          const m: Record<string, string> = {};
          for (const [ticker, meta] of Object.entries(r.data as Record<string, { name?: string }>)) {
            if (meta.name && meta.name !== ticker) m[ticker] = meta.name;
          }
          _cache = m;
        })
        .catch(() => { _cache = {}; })
        .finally(() => { setNames(_cache); });
    } else {
      void _inflight.finally(() => { setNames(_cache); });
    }
  }, []);

  return (ticker: string) => names?.[ticker] ?? null;
}
